import {
  Component,
  effect,
  ElementRef,
  input,
  output,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-calendar',
  imports: [MatIconModule],
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.css', '../../styles/layout.css'],
})
export class CalendarComponent {
  @ViewChild('calendarDates') calendarDates!: ElementRef;

  events = input<any[]>();
  blocked = input<any[]>();
  type = input<string>();
  unavailabilityDays = new Map<number, string>();
  // Emitimos true si hay días seleccionados, false si no hay ninguno
  hasSelection = output<boolean>();
  // New output to emit month changes
  monthChanged = output<{ month: number; year: number }>();

  date: Date = new Date();
  year: number = this.date.getFullYear();
  month: number = this.date.getMonth();

  months: string[] = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];

  daysInMonth: { day: number; class: string }[] = [];
  weekDays: string[] = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

  eventDays: number[] = [];
  blockedDays: number[] = [];
  selectedDates: { year: number; month: number; day: number }[] = [];

  // New arrays to track different vacation types
  approvedVacationDays: number[] = [];
  approvedPersonalVacationDays: number[] = [];
  pendingVacationDays: number[] = [];

  constructor() {
    // Watch for changes in events input and regenerate calendar
    effect(() => {
      // Access the signals to track them
      this.events();
      this.blocked();

      // Reprocess and regenerate calendar when events or blocked days change
      this.processEvents();
      this.processBlockedDays();
      this.generateCalendar();
    });
  }

  ngOnInit() {
    // Emit initial month
    this.monthChanged.emit({ month: this.month, year: this.year });
  }

  // Process events from input to set eventDays
  processEvents() {
    // Use nullish coalescing to provide an empty array if events is undefined
    const currentEvents = this.events() ?? [];

    // Reset all event arrays
    this.eventDays = [];
    this.approvedVacationDays = [];
    this.approvedPersonalVacationDays = [];
    this.pendingVacationDays = [];
    this.unavailabilityDays = new Map();

    if (currentEvents.length > 0) {
      // console.debug('Processing events', currentEvents);

      currentEvents.forEach((event) => {
        const eventDate = new Date(event.date);

        // Check if it's in the current month/year
        if (
          eventDate.getMonth() === this.month &&
          eventDate.getFullYear() === this.year
        ) {
          const day = eventDate.getDate();

          // Categorize by type if it exists, otherwise add to generic eventDays
          if (event.type === 'approved-vacation') {
            this.approvedVacationDays.push(day);
          } else if (event.type === 'approved-personal-vacation') {
            this.approvedPersonalVacationDays.push(day);
          } else if (event.type === 'pending-vacation') {
            this.pendingVacationDays.push(day);
          } else if (typeof event.type === 'string' && event.type.startsWith('unavailability-')) {
            this.unavailabilityDays.set(day, event.type);   
          } else {
            // Generic event (for backward compatibility)
            this.eventDays.push(day);
          }
        }
      });

      // console.debug('Event days for current month', this.eventDays);
    }
  }

  // Process blocked days from input to set blockedDays
  processBlockedDays() {
    // Use nullish coalescing to provide an empty array if blocked is undefined
    const currentBlocked = this.blocked() ?? [];

    if (currentBlocked.length > 0) {
      // console.debug('Processing blocked days', currentBlocked);

      this.blockedDays = currentBlocked
        .filter((block) => {
          // Parse the date and check if it's in the current month/year
          const blockDate = new Date(block.date);
          return (
            blockDate.getMonth() === this.month &&
            blockDate.getFullYear() === this.year
          );
        })
        .map((block) => {
          // Extract the day from the blocked date
          const blockDate = new Date(block.date);
          return blockDate.getDate();
        });

      // console.debug('Blocked days for current month', this.blockedDays);
    }
  }

  generateCalendar() {
    this.daysInMonth = [];

    // Get the first day of the month (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfMonth = new Date(this.year, this.month, 1).getDay();

    // Since weeks start on Monday in your calendar, adjust the index
    // Sunday (0) should be positioned as the 7th day, not the 1st
    const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

    // Add empty placeholders for days before the 1st of the month
    for (let i = 0; i < adjustedFirstDay; i++) {
      this.daysInMonth.push({ day: 0, class: 'empty-day' });
    }

    const lastDateOfMonth = new Date(this.year, this.month + 1, 0).getDate();

    // Days of the current month
    for (let i = 1; i <= lastDateOfMonth; i++) {
      let dayClass = 'current-month';

      // Apply vacation styles first (they have priority)
      if (this.approvedVacationDays.includes(i)) {
        dayClass = 'approved-vacation';
      } else if (this.approvedPersonalVacationDays.includes(i)) {
        dayClass = 'approved-personal-vacation';
      } else if (this.pendingVacationDays.includes(i)) {
        dayClass = 'pending-vacation';
      } else if (this.unavailabilityDays.has(i)) {
        dayClass = this.unavailabilityDays.get(i)!;
      } else if (this.eventDays.includes(i)) {
        dayClass = 'event';
      } else if (this.blockedDays.includes(i)) {
        dayClass = 'blocked';
      }

      // Check if this day is selected (using full date comparison)
      const isSelected = this.selectedDates.some(
        (date) =>
          date.year === this.year && date.month === this.month && date.day === i
      );

      // Apply selection class if selected
      if (isSelected) {
        if (this.type() === 'block') dayClass = 'selected-block';
        if (this.type() === 'unblock') dayClass = 'selected-unblock';
        if (this.type() === 'add') dayClass = 'selected-add';
        if (this.type() === 'delete') dayClass = 'selected-delete';
      }

      this.daysInMonth.push({ day: i, class: dayClass });
    }

    // Emitimos si hay días seleccionados o no
    this.hasSelection.emit(this.selectedDates.length > 0);
  }

  changeMonth(direction: number) {
    this.month += direction;
    if (this.month < 0) {
      this.month = 11;
      this.year--;
    } else if (this.month > 11) {
      this.month = 0;
      this.year++;
    }
    // When changing months, reprocess events and blocked days for the new month
    this.processEvents();
    this.processBlockedDays();
    this.generateCalendar();

    // Emit month change event
    this.monthChanged.emit({ month: this.month, year: this.year });
  }

  selectDay(day: number) {
    if (!this.type()) return;

    // Create a complete date object for comparison
    const currentDate = { year: this.year, month: this.month, day };

    // Check if the day is already blocked or has an event when type is 'block'
    if (this.type() === 'block') {
      // Don't allow selecting days that are already blocked
      if (this.blockedDays.includes(day)) {
        // console.log(`Day ${day} is already blocked and cannot be selected`);
        return;
      }

      // Don't allow selecting days that already have events
      if (this.eventDays.includes(day)) {
        // console.log(`Day ${day} already has an event and cannot be selected`);
        return;
      }
    }

    // For unblock mode, only allow selecting days that are actually blocked
    if (this.type() === 'unblock' && !this.blockedDays.includes(day)) {
      // console.log(`Day ${day} is not blocked and cannot be unblocked`);
      return;
    }

    // For delete mode, only allow selecting days that have events
    if (this.type() === 'delete' && !this.eventDays.includes(day)) {
      // console.log(`Day ${day} does not have an event and cannot be deleted`);
      return;
    }

    // Check if the date is already selected
    const index = this.selectedDates.findIndex(
      (d) =>
        d.year === currentDate.year &&
        d.month === currentDate.month &&
        d.day === currentDate.day
    );

    if (index !== -1) {
      // If found, remove it
      this.selectedDates.splice(index, 1);
    } else {
      // If not found, add it
      this.selectedDates.push(currentDate);
    }

    this.generateCalendar();
  }

  clearSelectedDays() {
    this.selectedDates = [];
    this.generateCalendar();
    this.hasSelection.emit(false);
  }

  getSelectedDates(): string[] {
    return this.selectedDates.map((date) => {
      const jsDate = new Date(date.year, date.month, date.day);
      const day = jsDate.getDate().toString().padStart(2, '0');
      const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
      const year = jsDate.getFullYear();
      return `${day}/${month}/${year}`;
    });
  }

  // Add this method to your CalendarComponent class
  isSelectionDisabled(day: number): boolean {
    // Skip days from previous/next month
    if (!day) return true;

    // Check if day has any vacation event
    const hasVacationEvent =
      this.approvedVacationDays.includes(day) ||
      this.approvedPersonalVacationDays.includes(day) ||
      this.pendingVacationDays.includes(day) ||
      this.unavailabilityDays.has(day);

    // For 'block' type, disable blocked days and days with events (including vacation events)
    if (this.type() === 'block') {
      return (
        this.blockedDays.includes(day) ||
        this.eventDays.includes(day) ||
        hasVacationEvent
      );
    }

    // For 'unblock' type, disable days that are not blocked
    if (this.type() === 'unblock') {
      return !this.blockedDays.includes(day);
    }

    // For 'delete' type, disable days that don't have events (including vacation events)
    if (this.type() === 'delete') {
      return !this.eventDays.includes(day) && !hasVacationEvent;
    }

    // For 'add' type, disable days that are blocked or already have events (including vacation events)
    if (this.type() === 'add') {
      return (
        this.blockedDays.includes(day) ||
        this.eventDays.includes(day) ||
        hasVacationEvent
      );
    }

    return false;
  }
}
